import { createContext, FC, ReactNode, useContext } from 'react'
import { skipToken } from '@reduxjs/toolkit/query'

import { mapAbTests, overrideAbTests } from './abTestHelpers.helpers'
import {
  AbTestContextValue,
  defaultPostQuoteAbTestValues,
  defaultPreQuoteAbTestValues,
  postQuoteAbTestKeys,
  preQuoteAbTestKeys,
} from './abTests.types'
import Loader from 'components/Loader'

import { AbTestParams } from 'types'
import { useAbTestsQuery } from 'app/services'
import { getLocalStorageItem } from 'utils/localStorage'
import { addDatadogAbTestEvaluation } from 'utils/logging/logging'

export const LOCAL_STORAGE_KEY = 'ABTEST_KEYS'

const AbTestContext = createContext<AbTestContextValue | null>(null)

interface Props {
  preQuoteKeys: Pick<AbTestParams, 'participantId' | 'userKey'>
  postQuoteKeys?: Pick<AbTestParams, 'participantId' | 'userKey'>
  children: ReactNode
}

export const AbTestsProvider: FC<Props> = ({ children, preQuoteKeys, postQuoteKeys }) => {
  const preQuoteAbTestsResponse = useAbTestsQuery(
    preQuoteAbTestKeys.length > 0
      ? {
          tests: preQuoteAbTestKeys,
          userKey: preQuoteKeys.userKey,
          participantId: preQuoteKeys.participantId,
        }
      : skipToken,
  )

  const postQuoteAbTestsResponse = useAbTestsQuery(
    postQuoteKeys && postQuoteAbTestKeys.length > 0
      ? {
          tests: postQuoteAbTestKeys,
          userKey: postQuoteKeys.userKey,
          participantId: postQuoteKeys.participantId,
        }
      : skipToken,
  )

  if (preQuoteAbTestsResponse.isFetching || postQuoteAbTestsResponse.isFetching) {
    return <Loader />
  }

  const abTestsOverrides = getLocalStorageItem(LOCAL_STORAGE_KEY)

  let preQuoteAbTests = mapAbTests({
    isSuccess: preQuoteAbTestsResponse.isSuccess,
    abTestsData: preQuoteAbTestsResponse.data,
    defaultAbTests: defaultPreQuoteAbTestValues,
  })

  let postQuoteAbTests = mapAbTests({
    isSuccess: postQuoteAbTestsResponse.isSuccess,
    abTestsData: postQuoteAbTestsResponse.data,
    defaultAbTests: defaultPostQuoteAbTestValues,
  })

  const preQuoteOverrides = { ...abTestsOverrides }
  const postQuoteOverrides = { ...abTestsOverrides }

  if (abTestsOverrides !== null) {
    preQuoteAbTests = overrideAbTests(preQuoteAbTests, preQuoteAbTestKeys, preQuoteOverrides)
  }

  if (abTestsOverrides !== null && postQuoteAbTests !== null) {
    postQuoteAbTests = overrideAbTests(postQuoteAbTests, postQuoteAbTestKeys, postQuoteOverrides)
  }

  const isPostQuoteAbTestsDefault =
    postQuoteKeys?.userKey === undefined || !postQuoteAbTestsResponse.data

  addDatadogAbTestEvaluation({
    ...preQuoteAbTests,
    ...(isPostQuoteAbTestsDefault ? {} : postQuoteAbTests),
  })

  return (
    <AbTestContext.Provider
      value={{
        preQuoteAbTests,
        postQuoteAbTests,
        isPostQuoteAbTestsDefault,
      }}
    >
      {children}
    </AbTestContext.Provider>
  )
}

export const useAbTests = () => {
  const abTestsValue = useContext(AbTestContext)

  if (abTestsValue === null) {
    throw new Error("'useAbTests' should have 'AbTestsProvider' as a parent")
  }

  return abTestsValue
}
